import { computeLoopAccess } from '../hooks/prioritization/useOrgAwareCatchball';
import type { CatchballLoop } from '../types/prioritization/catchballLoop';
import type { OrgUnit } from '../types/prioritization/enterpriseIdentity';
import type { ResponsibilityCard } from '../types/prioritization/responsibility';

type CycleSummary = {
  id: string;
  name: string;
  orgScopeNodeId?: string;
};

function deriveCycleIdFromRoute(route: string | null | undefined): string | null {
  if (!route) return null;
  const match = route.match(/\/prioritization\/(?:cascade|catchball)\/(?:inbox|dashboard|compose|workbench)\/([^/?#]+)/);
  return match?.[1] ?? null;
}

function buildRecipientLabel(loop: CatchballLoop, orgUnits: Map<string, OrgUnit>): string | null {
  const recipientIds = loop.intent?.recipientOrgUnitIds ?? [];
  if (recipientIds.length === 0) return null;

  const names = recipientIds
    .map((id) => orgUnits.get(id)?.displayName ?? null)
    .filter((name): name is string => Boolean(name));

  if (names.length === 0) return null;
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

export function buildFallbackCatchballResponsibilities(args: {
  loops: CatchballLoop[];
  cycles: CycleSummary[];
  currentUserId: string | null;
  orgUnits: Map<string, OrgUnit>;
  userNodeIndex: Map<string, string>;
  existingResponsibilities: ResponsibilityCard[];
  cycleId?: string;
}): ResponsibilityCard[] {
  const {
    loops,
    cycles,
    currentUserId,
    orgUnits,
    userNodeIndex,
    existingResponsibilities,
    cycleId,
  } = args;

  if (!currentUserId) return [];

  const cyclesById = new Map(cycles.map((cycle) => [cycle.id, cycle]));
  const coveredCycleIds = new Set(
    existingResponsibilities
      .filter((card) => card.scope === 'CATCHBALL' && card.classification === 'REQUIRED')
      .flatMap((card) => [
        deriveCycleIdFromRoute(card.deep_link),
        ...card.valid_actions.map((action) => deriveCycleIdFromRoute(action.route)),
      ])
      .filter((entry): entry is string => Boolean(entry))
  );

  return loops
    .filter((loop) => !cycleId || loop.cycleId === cycleId)
    .filter((loop) => loop.state !== 'closed')
    .filter((loop) => !coveredCycleIds.has(loop.cycleId))
    .filter((loop) => {
      const cycle = cyclesById.get(loop.cycleId);
      return computeLoopAccess(loop, currentUserId, orgUnits, userNodeIndex, cycle?.orgScopeNodeId).canActOnLoop;
    })
    .map((loop) => {
      const cycle = cyclesById.get(loop.cycleId);
      const recipientLabel = buildRecipientLabel(loop, orgUnits);
      const deepLink = `/prioritization/cascade/workbench/${loop.cycleId}/${loop.id}`;

      return {
        id: `catchball-fallback-${loop.id}`,
        classification: 'REQUIRED',
        scope: 'CATCHBALL',
        subject: `${cycle?.name || 'Catchball'} · Loop ${loop.loopNumber}`,
        why: recipientLabel
          ? `The ball is currently with ${recipientLabel}. Open the workbench to continue the handoff.`
          : 'The ball is currently with your level. Open the workbench to continue the handoff.',
        deep_link: deepLink,
        valid_actions: [
          {
            action: 'OPEN_CATCHBALL_WORKBENCH',
            label: 'Open loop workspace',
            route: deepLink,
          },
        ],
        diagnostics: [],
        is_valid: true,
        invalid_reason: null,
        created_at: loop.updatedAt.toISOString(),
        deadline: loop.intent?.dueDate ?? null,
      } satisfies ResponsibilityCard;
    });
}
